import { ref, computed, reactive } from 'vue';
import type { CompareExpertsResponse } from '~/api/v2/users/UserRepository';

export type CompareContext = 'search' | 'search-mission' | 'mission-wizard' | 'passd';

interface UseExpertComparisonOptions {
  context: CompareContext;
  missionId?: () => string | undefined; // getter, car la mission peut changer (mode mission de la recherche)
}

const MIN_COMPARE = 2;
const MAX_COMPARE = 5;

export function useExpertComparison(options: UseExpertComparisonOptions) {
  const { $api } = useNuxtApp();

  const selectedIds = ref<string[]>([]);
  const isOpen = ref(false);
  const loading = ref(false);
  const error = ref<string | null>(null);
  const data = ref<CompareExpertsResponse | null>(null);

  const canCompare = computed(() => selectedIds.value.length >= MIN_COMPARE && selectedIds.value.length <= MAX_COMPARE);
  const isFull = computed(() => selectedIds.value.length >= MAX_COMPARE);

  const isSelected = (id: string) => selectedIds.value.includes(id);

  function toggle(id: string) {
    const idx = selectedIds.value.indexOf(id);
    if (idx !== -1) {
      selectedIds.value.splice(idx, 1);
    } else if (selectedIds.value.length < MAX_COMPARE) {
      selectedIds.value.push(id);
    }
    // Si déjà plein, on n'ajoute rien (l'UI affiche "décocher pour changer").
  }

  function clear() {
    selectedIds.value = [];
  }

  async function open() {
    if (!canCompare.value) return;
    loading.value = true;
    error.value = null;
    isOpen.value = true;
    try {
      const missionId = options.missionId?.();
      data.value = await $api().v2.users.compare(selectedIds.value, missionId);
    } catch (e) {
      console.error('Failed to fetch comparison:', e);
      error.value = 'load_error';
    } finally {
      loading.value = false;
    }
  }

  // Ouvre la comparaison sur un jeu d'ids fourni de l'extérieur (ex. la sélection
  // existante d'experts dans les étapes de création), sans passer par les checkboxes.
  async function openWith(ids: string[]) {
    selectedIds.value = [...ids].slice(0, MAX_COMPARE);
    await open();
  }

  async function retry() {
    await open();
  }

  function close() {
    isOpen.value = false;
  }

  // `reactive` déballe les refs en accès direct (`comparison.isFull`, `comparison.data`,
  // `comparison.selectedIds.length`) aussi bien dans les templates que dans le script,
  // ce qui évite les pièges de non-déballage des refs imbriquées d'un objet simple.
  return reactive({
    context: options.context,
    max: MAX_COMPARE,
    selectedIds,
    isOpen,
    loading,
    error,
    data,
    canCompare,
    isFull,
    isSelected,
    toggle,
    clear,
    open,
    openWith,
    retry,
    close,
  });
}
